import { RowNode } from './entities/rowNode';
import { Column } from './entities/column';
import { ColDef } from './entities/colDef';
import { GridApi } from './gridApi';
import { ColumnApi } from './columnController/columnApi';
import { OriginalColumnGroup } from './entities/originalColumnGroup';
import { FilterRequestSource } from './filter/filterManager';
import { ChartOptions, ChartType } from './interfaces/iChartOptions';
import { IFilterComp } from './interfaces/iFilter';
import { CellRange, CellRangeParams } from './interfaces/iRangeController';
import { ChartModel } from './interfaces/IChartService';
import { ServerSideTransactionResult } from "./interfaces/serverSideTransaction";
import { RowNodeTransaction } from "./interfaces/rowNodeTransaction";
export { Events } from './eventKeys';

export interface ModelUpdatedEvent extends DyGridEvent {
  /** If true, the grid will try and animate the rows to the new positions */
  animate: boolean | undefined;
  /** If true, the grid has new data loaded, eg user called setRowData(), otherwise
   * it's the same data but sorted or filtered, in which case this is true, and rows
   * can animate around (eg rowNode id 24 is the same row node as last time). */
  keepRenderedRows: boolean | undefined;
  /** If true, then this update was a result of setRowData() getting called. This
   * gets the grid to scroll to the top again. */
  newData: boolean | undefined;
  /** True when pagination and a new page is navigated to. */
  newPage: boolean;
}

export interface DyEvent {
  type: string;
}

export interface DyGridEvent extends DyEvent {
  api: GridApi;
  columnApi: ColumnApi;
}

export interface ToolPanelVisibleChangedEvent extends DyGridEvent {
  source: string | undefined;
}

export interface AnimationQueueEmptyEvent extends DyGridEvent { }

export interface ColumnPivotModeChangedEvent extends DyGridEvent { }

export interface VirtualColumnsChangedEvent extends DyGridEvent { }

export interface ColumnEverythingChangedEvent extends DyGridEvent {
  source: string;
}

export interface NewColumnsLoadedEvent extends DyGridEvent { }

export interface GridColumnsChangedEvent extends DyGridEvent { }

export interface DisplayedColumnsChangedEvent extends DyGridEvent { }

export interface RowDataChangedEvent extends DyGridEvent { }

export interface RowDataUpdatedEvent extends DyGridEvent { }

export interface PinnedRowDataChangedEvent extends DyGridEvent { }

export interface SelectionChangedEvent extends DyGridEvent { }

export interface FilterChangedEvent extends DyGridEvent {
  afterDataChange?: boolean;
  afterFloatingFilter?: boolean;
}

export interface FilterModifiedEvent extends DyGridEvent {
  filterInstance: IFilterComp;
  column: Column;
}

export interface FilterOpenedEvent extends DyGridEvent {
  column: Column | OriginalColumnGroup;
  source: FilterRequestSource;
  eGui: HTMLElement;
}

export interface SortChangedEvent extends DyGridEvent { }

export interface GridReadyEvent extends DyGridEvent { }

export interface DisplayedColumnsWidthChangedEvent extends DyGridEvent { } // not documented
export interface ColumnHoverChangedEvent extends DyGridEvent { } // not documented
export interface BodyHeightChangedEvent extends DyGridEvent { } // not documented

// this event is 'odd one out' as it should have properties for all the properties
// in gridOptions that can be bound by the framework. for example, the gridOptions
// has 'rowData', so this property should have 'rowData' also, so that when the row
// data changes via the framework bound property, this event has that attribute set.
export interface ComponentStateChangedEvent extends DyGridEvent { }

export interface DragEvent extends DyGridEvent {
  type: string;
  target: HTMLElement;
}

export interface DragStartedEvent extends DragEvent { }

export interface DragStoppedEvent extends DragEvent { }

// For internal use only.
// This event allows us to detect when other inputs in the same named group are changed, so for example we can ensure
// that only one radio button in the same group is selected at any given time.
export interface CheckboxChangedEvent extends DyEvent {
  id: string;
  name: string;
  selected?: boolean;
  previousValue: boolean | undefined;
}

export interface GridSizeChangedEvent extends DyGridEvent {
  clientWidth: number;
  clientHeight: number;
}

export interface RowDragEvent extends DyGridEvent {
  node: RowNode;
  nodes: RowNode[];
  y: number;
  vDirection: string;
  event: MouseEvent;
  overIndex: number;
  overNode: RowNode;
}

export interface RowDragEnterEvent extends RowDragEvent { }

export interface RowDragEndEvent extends RowDragEvent { }

export interface RowDragMoveEvent extends RowDragEvent { }

export interface RowDragLeaveEvent extends RowDragEvent { }

export interface PasteStartEvent extends DyGridEvent {
  source: string;
}

export interface PasteEndEvent extends DyGridEvent {
  source: string;
}

export interface FillStartEvent extends DyGridEvent {
}

export interface FillEndEvent extends DyGridEvent {
  initialRange: CellRange;
  finalRange: CellRange;
}

export interface ViewportChangedEvent extends DyGridEvent {
  firstRow: number;
  lastRow: number;
}

export interface FirstDataRenderedEvent extends DyGridEvent {
  firstRow: number;
  lastRow: number;
}

export interface RangeSelectionChangedEvent extends DyGridEvent {
  id?: string;
  finished: boolean;
  started: boolean;
}

export interface ChartCreated extends DyGridEvent {
  chartId: string;
  chartModel: ChartModel;
}

export interface ChartRangeSelectionChanged extends DyGridEvent {
  id: string;
  chartId: string;
  cellRange: CellRangeParams;
}

export interface ChartOptionsChanged extends DyGridEvent {
  chartId: string;
  chartType: ChartType;
  chartThemeName: string;
  chartOptions: ChartOptions<any>;
}

export interface ChartDestroyed extends DyGridEvent {
  chartId: string;
}

export interface ColumnGroupOpenedEvent extends DyGridEvent {
  columnGroup: OriginalColumnGroup;
}

export interface ItemsAddedEvent extends DyGridEvent {
  items: RowNode[];
}

export type ScrollDirection = 'horizontal' | 'vertical';

export interface BodyScrollEvent extends DyGridEvent {
  direction: ScrollDirection;
  left: number;
  top: number;
}

// not documented
export interface FlashCellsEvent extends DyGridEvent {
  cells: any;
}

export interface PaginationChangedEvent extends DyGridEvent {
  animate?: boolean;
  keepRenderedRows?: boolean;
  newData?: boolean;
  newPage: boolean;
}

export interface PaginationPixelOffsetChangedEvent extends DyGridEvent {
}

// this does not extent CellEvent as the focus service doesn't keep a reference to
// the rowNode.
export interface CellFocusedEvent extends DyGridEvent {
  rowIndex: number | null;
  column: Column | null;
  rowPinned?: string | null;
  forceBrowserFocus?: boolean;
  // floating is for backwards compatibility, this is the same as rowPinned.
  // this is because the focus service doesn't keep references to rowNodes
  // as focused cell is identified by rowIndex - thus when the user re-orders
  // or filters, the focused cell stays with the index, but the node can change.
  floating: string | null;
}

export interface ExpandCollapseAllEvent extends DyGridEvent {
  source: string;
}

/**---------------*/
/** COLUMN EVENTS */
/**---------------*/

export type ColumnEventType =
  "sizeColumnsToFit" |
  "autosizeColumns" |
  "alignedGridChanged" |
  "filterChanged" |
  "filterDestroyed" |
  "gridOptionsChanged" |
  "gridInitializing" |
  "toolPanelDragAndDrop" |
  "toolPanelUi" |
  "uiColumnMoved" |
  "uiColumnResized" |
  "uiColumnDragged" |
  "uiColumnExpanded" |
  "uiColumnSorted" |
  "contextMenu" |
  "columnMenu" |
  "rowModelUpdated" |
  "api" |
  "flex" |
  "pivotChart";

export interface ColumnEvent extends DyGridEvent {
  column: Column | null;
  columns: Column[] | null;
  source: ColumnEventType;
}

export interface ColumnResizedEvent extends ColumnEvent {
  finished: boolean;
  flexColumns: Column[] | null;
}

export interface ColumnPivotChangedEvent extends ColumnEvent { }

export interface ColumnRowGroupChangedEvent extends ColumnEvent { }

export interface ColumnValueChangedEvent extends ColumnEvent { }

export interface ColumnMovedEvent extends ColumnEvent {
  toIndex?: number;
}

export interface ColumnVisibleEvent extends ColumnEvent {
  visible?: boolean;
}

export interface ColumnPinnedEvent extends ColumnEvent {
  pinned: string | null;
}

/**------------*/

/** ROW EVENTS */
/**------------*/
export interface RowEvent extends DyGridEvent {
  node: RowNode;
  data: any;
  rowIndex: number | null;
  rowPinned: string;
  context: any;
  event?: Event | null;
}

export interface RowGroupOpenedEvent extends RowEvent {
  expanded: boolean;
}

export interface RowValueChangedEvent extends RowEvent { }

export interface RowSelectedEvent extends RowEvent { }

export interface VirtualRowRemovedEvent extends RowEvent { }

export interface RowClickedEvent extends RowEvent { }

export interface RowDoubleClickedEvent extends RowEvent { }

export interface RowEditingStartedEvent extends RowEvent { }

export interface RowEditingStoppedEvent extends RowEvent { }

/**------------*/

/** CELL EVENTS */
/**------------*/
export interface CellEvent extends RowEvent {
  column: Column;
  colDef: ColDef;
  value: any;
}

export interface CellKeyDownEvent extends CellEvent { }

export interface CellKeyPressEvent extends CellEvent { }

export interface CellClickedEvent extends CellEvent { }

export interface CellMouseDownEvent extends CellEvent { }

export interface CellDoubleClickedEvent extends CellEvent { }

export interface CellMouseOverEvent extends CellEvent { }

export interface CellMouseOutEvent extends CellEvent { }

export interface CellContextMenuEvent extends CellEvent { }

export interface CellEditingStartedEvent extends CellEvent { }

export interface CellEditingStoppedEvent extends CellEvent {
  oldValue: any;
  newValue: any;
}

export interface CellValueChangedEvent extends CellEvent {
  oldValue: any;
  newValue: any;
  source: string | undefined;
}

export interface AsyncTransactionsFlushed extends DyGridEvent {
  results: (RowNodeTransaction | ServerSideTransactionResult)[];
}

// not documented, was put in for CS - more thought needed of how server side grouping / pivoting
// is done and how these should be used before we fully document and share with the world.
export interface ColumnRequestEvent extends DyGridEvent {
  columns: Column[];
}

export interface ColumnRowGroupChangeRequestEvent extends ColumnRequestEvent { }

export interface ColumnPivotChangeRequestEvent extends ColumnRequestEvent { }

export interface ColumnValueChangeRequestEvent extends ColumnRequestEvent { }

export interface ColumnAggFuncChangeRequestEvent extends ColumnRequestEvent {
  aggFunc: any;
}

export interface ScrollVisibilityChangedEvent extends DyGridEvent { } // not documented

export interface StoreUpdatedEvent extends DyEvent { } // not documented
